import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import {
  HomePageComponent,
  MinRoleCheckGuard,
  HasPermissionGuard,
  ClientNoteComponent,
  ClientListComponent,
  ClientSearchComponent,
  ClientReportsHomeComponent,
  CanDeactivatePersonGuard,
  PeopleSearchComponent,
  PersonEditorWrapperComponent,
  PeopleListComponent,
  AccessLevels,
  ClientUserManagementComponent,
  PayrollLandingPageComponent,
  PayrollGridComponent,
  CanDeactivatePayrollGuard,
  CanDeactivateManualPayrollGuard,
  PayrollPreviewComponent,
  AutoPayrollGridComponent,
  AutoPayrollHomeComponent,
  CompanyHomeComponent,
  ArticleFullPageComponent,
  ReportsListComponent,
  PayrollWipSupervisorComponent,
  PayrollWipCSRComponent,
  AchScanComponent,
  ClientNavWrapperComponent,
  TimeclockCheckComponent,
  ManualChecksComponent,
  PayrollStartComponent,
  PageNotFoundComponent,
  ClientHomeComponent,
  ManualPayrollPreviewComponent,
  ManualPayrollComponent,
  FeatureFlagGuard,
  PreferencesHomeComponent,
  PassThruWrapperComponent,
  PrintChecksStartComponent,
  CheckSelectionComponent,
  PayrollCorrectionDetailsComponent,
  PayrollCorrectionsComponent,
  canDeactivateCorrectionsGuard,
  PayrollCorrectionsPreviewComponent,
  GeneralLedgerExportComponent,
  GlMappingComponent,
  ApproverHomeComponent,
  ClientTimeOffHomeComponent,
  AclGuard,
  canEnableTimeOffGuard,
  ClientCodeCasingGuard
} from 'ng-shared-library';
import { canDeactivateGLGuard } from '../../../ng-shared-library/src/lib/guards/can-deactivate-general-ledger.guard';

import { environment } from '../environments/environment';
import { AuthGuard } from './guards/auth.guard';

let routes: Routes = [
  {
    path: '',
    canActivateChild: [AuthGuard],
    children: [
      {
        path: '',
        component: HomePageComponent,
        canActivate: [MinRoleCheckGuard],
        data: { roles: ['CSR1', 'Banking'], title: 'CSR Home' }
      },
      {
        path: 'home',
        component: HomePageComponent,
        canActivate: [MinRoleCheckGuard],
        data: { roles: ['CSR1', 'Banking'], title: 'CSR Home' },
        pathMatch: 'full'
      },
      {
        path: 'clients',
        children: [
          {
            path: '',
            component: ClientListComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1', 'Banking'], title: 'Client List' },
            pathMatch: 'full'
          },
          {
            path: 'search/:search_query',
            component: ClientSearchComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1', 'Banking'], title: 'Client Search' },
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'knowledge-base/article/:id',
        component: ArticleFullPageComponent,
        data: { title: 'Knowledge Base Full Article' }
        // TODO: determine which (if any) security permissions need to be set
      },
      {
        path: 'reports',
        children: [
          {
            path: '',
            component: ReportsListComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1', 'Banking'], title: 'Report List' }
          },
          {
            path: 'ach-scan',
            component: AchScanComponent,
            canActivate: [HasPermissionGuard, MinRoleCheckGuard],
            data: {
              permission: 'corporate_reports_ach_scan',
              access: AccessLevels.Read,
              roles: ['CSR1', 'Banking'],
              title: 'ACH Scan Report'
            }
          },
          {
            path: 'payroll-wip-csr',
            component: PayrollWipCSRComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1', 'Banking'], title: 'Payroll WIP Report' },
            pathMatch: 'full'
          },
          {
            path: 'payroll-wip-supervisor',
            component: PayrollWipSupervisorComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1', 'Banking'], title: 'Payroll WIP Report' },
            pathMatch: 'full'
          }
        ]
      },
      {
        //
        // Client Routes
        //
        path: 'client/:client_code',
        canActivateChild: [ClientCodeCasingGuard],
        component: ClientNavWrapperComponent,
        children: [
          {
            path: '',
            component: ClientHomeComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1', 'Banking'], title: 'Client Home' },
            pathMatch: 'full'
          },
          {
            path: 'approvals',
            component: ApproverHomeComponent
          },
          {
            path: 'time-off',
            component: ClientTimeOffHomeComponent,
            canActivate: [MinRoleCheckGuard, canEnableTimeOffGuard],
            data: {
              roles: ['CSR1', 'Banking']
            }
          },
          {
            path: 'company',
            component: CompanyHomeComponent,
            data: { title: 'Company Home' }
          },
          {
            path: 'notes',
            component: ClientNoteComponent,
            canActivate: [MinRoleCheckGuard],
            data: { title: 'Client Notes', roles: ['CSR1', 'Banking'], activeTab: 'Notes' }
          },
          {
            path: 'settings',
            component: PreferencesHomeComponent,
            data: { title: 'Settings' }
          },
          {
            path: 'reports',
            component: ClientReportsHomeComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1', 'Banking'], title: 'Client Reports Home' }
          },
          {
            path: 'people',
            children: [
              {
                path: '',
                component: PeopleListComponent,
                canActivate: [MinRoleCheckGuard],
                data: { roles: ['CSR1', 'Banking'], title: 'People List', activeTab: 'People' }
              },
              {
                path: 'search/:search_query',
                component: PeopleSearchComponent,
                canActivate: [MinRoleCheckGuard],
                data: { roles: ['CSR1', 'Banking'], title: 'People Search' },
                pathMatch: 'full'
              }
            ]
          },
          {
            path: 'person',
            data: { activeTab: 'People' },
            children: [
              {
                path: '',
                component: PageNotFoundComponent
              },
              {
                path: 'add',
                pathMatch: 'full',
                component: PersonEditorWrapperComponent,
                canActivate: [HasPermissionGuard, MinRoleCheckGuard],
                canDeactivate: [CanDeactivatePersonGuard],
                data: {
                  permission: 'employee',
                  access: AccessLevels.Read,
                  roles: ['CSR1', 'Banking'],
                  title: 'Add Person'
                }
              },
              {
                path: ':id',
                component: PersonEditorWrapperComponent,
                canActivate: [HasPermissionGuard, MinRoleCheckGuard],
                canDeactivate: [CanDeactivatePersonGuard],
                data: {
                  permission: 'employee',
                  access: AccessLevels.Read,
                  roles: ['CSR1', 'Banking'],
                  title: 'Edit Person'
                }
              }
            ]
          },
          {
            path: 'payroll-landing',
            component: PayrollLandingPageComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1'], title: 'Payroll Landing Page', activeTab: 'Payroll' }
          },
          {
            path: 'manual-payroll/:year/:seqNo',
            component: ManualPayrollComponent,
            canDeactivate: [CanDeactivateManualPayrollGuard],
            canActivate: [MinRoleCheckGuard, FeatureFlagGuard],
            data: {
              aclChecks: ['Client Portal Admin', 'RunPayroll'],
              clientCodeParam: 'client_code',
              activeTab: 'Payroll',
              roles: ['CSR1'],
              title: 'Manual Payroll',
              featureFlag: 'EnableManualPayroll'
            }
          },
          {
            path: 'manual-payroll-preview/:year/:seqNo',
            component: ManualPayrollPreviewComponent,
            canDeactivate: [CanDeactivateManualPayrollGuard],
            canActivate: [MinRoleCheckGuard, FeatureFlagGuard],
            data: {
              aclChecks: ['Client Portal Admin', 'RunPayroll'],
              clientCodeParam: 'client_code',
              activeTab: 'Payroll',
              roles: ['CSR1'],
              title: 'Manual Payroll Preview',
              featureFlag: 'EnableManualPayroll'
            }
          },

          {
            path: 'general-ledger',
            component: PassThruWrapperComponent,
            children: [
              {
                canDeactivate: [canDeactivateGLGuard],
                path: '',
                component: GeneralLedgerExportComponent
              }
            ]
          },
          {
            path: 'payroll-corrections',
            component: PayrollCorrectionsComponent,
            canDeactivate: [canDeactivateCorrectionsGuard],
            canActivate: [MinRoleCheckGuard, FeatureFlagGuard],
            data: {
              aclChecks: ['Client Portal Admin', 'RunPayroll'],
              clientCodeParam: 'client_code',
              activeTab: 'Payroll',
              roles: ['CSR1'],
              title: 'Run Correction Payroll',
              featureFlag: 'EnableCorrectionPayrollStart'
            }
          },
          {
            path: 'payroll-corrections/:year/:seqNo/person/:personId/:paylineId',
            component: PayrollCorrectionDetailsComponent,
            canDeactivate: [canDeactivateCorrectionsGuard],
            canActivate: [MinRoleCheckGuard, FeatureFlagGuard],
            data: {
              aclChecks: ['Client Portal Admin', 'RunPayroll'],
              clientCodeParam: 'client_code',
              activeTab: 'Payroll',
              roles: ['CSR1'],
              title: 'Run Correction Payroll',
              featureFlag: 'EnableCorrectionPayrollStart'
            }
          },
          {
            path: 'payroll-corrections-preview/:year/:seqNo',
            component: PayrollCorrectionsPreviewComponent,
            canDeactivate: [canDeactivateCorrectionsGuard],
            canActivate: [MinRoleCheckGuard, FeatureFlagGuard],
            data: {
              aclChecks: ['Client Portal Admin', 'RunPayroll'],
              clientCodeParam: 'client_code',
              activeTab: 'Payroll',
              roles: ['CSR1'],
              title: 'Correction Payroll Preview',
              featureFlag: 'EnableCorrectionPayrollStart'
            }
          },
          {
            path: 'autopayroll',
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1'], activeTab: 'Payroll' },
            children: [
              {
                path: '',
                component: AutoPayrollHomeComponent,
                canActivate: [MinRoleCheckGuard],
                data: { roles: ['CSR1'], title: 'Auto Payroll Start' }
              },
              {
                path: ':id',
                component: AutoPayrollGridComponent,
                canActivate: [MinRoleCheckGuard],
                data: { roles: ['CSR1'], title: 'Auto Payroll Grid' }
              }
            ]
          },
          {
            path: 'payroll',
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1'], title: 'Payroll Start', activeTab: 'Payroll' },
            children: [
              {
                path: '',
                component: PayrollStartComponent,
                canActivate: [MinRoleCheckGuard],
                data: { roles: ['CSR1'], title: 'Payroll Start' }
              }
              // FIXME: due to rendering issues with the person editor
              // and left nav inside the payroll grid (bugs 18811 & 19060)
              // this route is not defined as a child. The long term solution
              // would be to render the person editor inside a ng bootstrap modal
              // and/or determine why there are nested scroll containers elements
              // introduced when the routes were set as children
              // {
              //   path: ':year/:seqNo',
              //   component: PayrollGridComponent,
              //   canDeactivate: [CanDeactivatePayrollGuard],
              //   canActivate: [MinRoleCheckGuard],
              //   data: { roles: ['CSR1'], title: 'Payroll Grid' }
              // }
            ]
          },
          {
            path: 'timeclock-check',
            component: TimeclockCheckComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1'], title: 'Check for TimeClock', activeTab: 'Payroll' },
            pathMatch: 'full'
          },
          {
            path: 'manual-checks',
            component: ManualChecksComponent,
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1'], title: 'Manual Checks Listing', activeTab: 'Payroll' },
            pathMatch: 'full'
          },
          {
            path: 'payroll-preview/:year/:seqNo',
            component: PayrollPreviewComponent,
            canDeactivate: [CanDeactivatePayrollGuard],
            canActivate: [MinRoleCheckGuard],
            data: { roles: ['CSR1'], title: 'Payroll Preview', activeTab: 'Payroll' }
          }
        ]
      }
    ]
  },
  // FIXME: due to rendering issues with the person editor
  // and left nav inside the payroll grid (bugs 18811 & 19060)
  // this route is not defined as a child. The long term solution
  // would be to render the person editor inside a ng bootstrap modal
  // and/or determine why there are nested scroll containers elements
  // introduced when the routes were set as children
  {
    path: 'client/:client_code/payroll/:year/:seqNo',
    component: PayrollGridComponent,
    canDeactivate: [CanDeactivatePayrollGuard],
    canActivate: [MinRoleCheckGuard, ClientCodeCasingGuard],
    data: { roles: ['CSR1'], title: 'Payroll Grid' }
  }
];

if (environment.EnableUserManagement) {
  routes = [
    {
      path: 'client/:client_code/users',
      component: ClientNavWrapperComponent,
      canActivateChild: [ClientCodeCasingGuard],
      children: [
        {
          path: '',
          canActivate: [MinRoleCheckGuard],
          data: { roles: ['CSR1', 'Banking'], title: 'User Management' },
          pathMatch: 'full',
          component: ClientUserManagementComponent
        }
      ]
    },
    ...routes
  ];
}

@NgModule({
  imports: [RouterModule.forRoot(routes, { paramsInheritanceStrategy: 'always' })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
